<template>
  <v-container fluid class="ma-4 px-8">
    <v-row>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0" v-if="showPartner()">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
          @change="getPrograms()"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.application_status"
          :items="statuses"
          outlined
          dense
          hide-details="auto"
          label="Status"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
        ></v-text-field>
      </v-col>
      <v-col
        cols="12"
        md="2"
        class="py-0 my-0"
        v-if="this.currentUser.role === 'Partner'"
      >
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          hide-details="auto"
          label="Employee ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-btn class="accent white--text" :loading="loading" @click="search">
          Search
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="admissionHeaders"
            :items="reportData"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.mobile_no`]="{ item }">
              {{ item.mobile_prefix }} {{ item.mobile_no }}
            </template>
            <template v-slot:[`item.view`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="
                  edit(
                    item.applicant_detail_id
                      ? item.applicant_detail_id
                      : item.id,
                    item.program_id,
                  )
                "
              >
                mdi-pencil
              </v-icon>
            </template>
            <template v-slot:[`item.pending_reason`]="{ item }">
              <div>
                <v-tooltip v-if="item.pendingHistory" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="mr-2"
                      color="accent"
                      v-bind="attrs"
                      v-on="on"
                      @click="getPendingHistory(item)"
                    >
                      mdi-dots-horizontal
                    </v-icon>
                  </template>
                  <span>There is no pending history</span>
                </v-tooltip>

                <!-- Fallback icon if no tooltip yet -->
                <v-icon
                  v-else
                  class="mr-2"
                  color="accent"
                  @click="getPendingHistory(item)"
                >
                  mdi-dots-horizontal
                </v-icon>
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="pendingHistoryDialog" width="800" persistent scrollable>
      <pendingHistory
        v-if="pendingHistoryDialog"
        @closeDialog="pendingHistoryDialog = false"
        :pendings="pendings"
        :applicantId="applicantId"
      />
    </v-dialog>
    <v-dialog v-model="dialog" fullscreen persistent no-click-animation>
      <formDialog
        v-if="dialog"
        @update="dialog = false"
        :qualifications="qualifications"
        :applicant="applicantDetail"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { admissionHeaders } from "@/constants/headers";
import pendingHistory from "@/components/applicant/pendingHistory.vue";
import { applicantPayload } from "@/helpers/payload";
import formDialog from "@/components/applicant/form.vue";
export default {
  components: { pendingHistory, formDialog },
  data() {
    return {
      form: {
        application_status: null,
        program_id: null,
        application_id: null,
      },
      rules,
      admissionHeaders,
      headers: [],
      statuses: [
        "Submitted",
        "Approved",
        "Rejected",
        "Admitted",
        "Provisionally Approved",
        "Pending",
        "Re-Submitted",
      ],
      programs: [],
      partners: [],
      reportData: [],
      loading: false,
      pendings: [],
      pendingHistoryDialog: false,
      batches: [],
      dialog: false,
      qualifications: []
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Admissions" }]);
    this.form.partner_id = this.currentUser.partner_id;
     const employeeHeader = {
      text: "Employee ID",
      value: "employee_id",
      class: "black--text font-weight-bold secondary",
    };
    const employee_id_present = admissionHeaders.find(
      (header) => header.value === "employee_id"
    );

    if (!employee_id_present) {
      admissionHeaders.splice(1, 0, employeeHeader);
    }

    await this.getPrograms();
    await this.generateBatchList();
    await this.showPartnerFilter();
    this.initPartnerAdmissions();
  },
  methods: {
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(
              value.toString(),
            )}`,
        )
        .join("&");
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
        this.form.year_batch = this.batches[0];
      } catch (error) {
        console.log(error);
      }
    },
    async initPartnerAdmissions() {
      try {
        this.loading = true;
        const queryString = this.objectToQueryParams({
          ...this.form,
        });
        const res = await this.$axios.get(
          `/get_applicant_details?${queryString}`,
        );
        this.reportData = res.data.data.sort((a, b) => a.id - b.id);
      } catch (error) {
        console.error("Fetching partner admissions failed");
      } finally {
        this.loading = false;
      }
    },
    search() {
      this.initPartnerAdmissions();
    },
    async getPrograms() {
      this.form.program_id = null;
      const url =
        this.currentUser.role === "Partner" ||
        this.currentUser.role === "Business"
          ? `/get_partner_programs/${this.form.partner_id}`
          : "/programs";
      await this.$axios.get(url).then((res) => {
        this.programs = res.data.data;
        if (this.programs.length === 1) {
          this.form.program_id = this.programs[0].id;
        }
      });
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getPendingHistory(item) {
      await this.$axios
        .get(`/pending_reasons?applicant_detail_id=${item.id}`)
        .then((res) => {
          this.pendings = res.data.data;
          if (this.pendings.length > 0) {
            this.pendingHistory = false;
            this.applicantId = item.application_id;
            this.$set(item, "pendingHistory", false);
            this.pendingHistoryDialog = true;
          } else {
            this.$set(item, "pendingHistory", true);
            this.pendingHistoryDialog = false;
            this.pendingHistory = true;
            this.applicantId = null;
          }
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    showPartner() {
      return (
        this.currentUser.role === "Business" &&
        this.partners.length > 1 &&
        this.currentUser.partner_level != "S"
      );
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async showPartnerFilter() {
      if (this.currentUser.partner_id != 1) {
        this.form.partner_id = this.currentUser.partner_id;
      }
      await this.getPartners();
      if (
        this.currentUser.role === "Business" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id,
        );
      }
    },
    async edit(applicant_detail_id, program_id) {
      const [program, applicant] = await Promise.all([
        this.$axios.get(`/programs/${program_id}`),
        this.$axios.get(`/applicant_details/${applicant_detail_id}`),
      ]);

      const applicantData = applicant.data.data;

      this.qualifications = program.data.data.qualifications;
      this.applicantDetail = applicantPayload(
        this.qualifications,
        applicantData,
      );

      this.dialog = true;
    },
  },
};
</script>

<style scoped>
.v-data-table::v-deep td {
  font-size: 12px !important;
  padding-right: 0px !important;
}

.v-data-table::v-deep th {
  font-size: 12px !important;
  padding-right: 0px !important;
}
</style>
